import React, { useState, useEffect } from 'react';
import { S } from '../styles';
// roleLabel: `owner` reads as Administrator; see helpers.js.
import { authHeaders, roleLabel } from '../utils/helpers';

const ROLES = ['owner', 'manager', 'tech', 'associate'];


export default function UserManagement({ theme, T, API }) {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Whether Lightspeed has actually answered. Separate from `employees` being
  // empty, which is also what "the call failed" and "Lightspeed is not
  // connected" look like — and those must not be mistaken for "this person is
  // not an employee".
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Former staff are collapsed by default. At a shop that hires seasonally
  // they accumulate every year, and the list you actually work with is the
  // people who are here now.
  const [showInactive, setShowInactive] = useState(false);

  const emptyForm = { username: '', email: '', password: '', role: 'tech', employee_id: '', first_name: '', last_name: '', canViewAllTechs: false };
  const [form, setForm] = useState(emptyForm);

 useEffect(() => {
  fetch(`${API}/api/users`, { headers: authHeaders(true) }).then(r => r.json())
    .then(u => { setUsers(Array.isArray(u) ? u : []); setLoading(false); })
    .catch(() => setLoading(false));
  // The employee list used to load only when a form was opened, so on a fresh
  // page load NOBODY resolved to a name — `employees` was still []. The list
  // needs it too, to show names and to judge who is still in Lightspeed.
  loadEmployees();
}, []);

  // One fetch, one place. `employeesLoaded` records that we actually have an
  // answer: an empty array because the call failed, or because Lightspeed is
  // not connected, must never be read as "these people are not employees".
  async function loadEmployees() {
    if (employeesLoaded) return;
    try {
      const res = await fetch(`${API}/api/users/employees`, { headers: authHeaders() });
      if (!res.ok) return;                       // 409 when Lightspeed is not connected
      const e = await res.json();
      if (!Array.isArray(e)) return;
      setEmployees(e);
      setEmployeesLoaded(true);
    } catch (_) { /* leave it unloaded — unknown, not empty */ }
  }

  async function openCreate() {
  setForm(emptyForm);
  setEditingUser(null);
  setError('');
  await loadEmployees();
  setShowForm(true);
}

async function openEdit(user) {
  setForm({
    username: user.username,
    email: user.email,
    password: '',
    role: user.role,
    employee_id: user.employee_id || '',
    canViewAllTechs: user.permissions?.canViewAllTechs || false,
  });
  setEditingUser(user);
  setError('');
  await loadEmployees();
  setShowForm(true);
}
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    try {
      const url = editingUser ? `${API}/api/users/${editingUser.id}` : `${API}/api/users`;
      const method = editingUser ? 'PUT' : 'POST';
      const body = { ...form };
      // `permissions` is the user's OVERRIDE set, not their effective
      // permissions — GET /api/users returns the raw column. Merge into it:
      // assigning a fresh object erases every other override they hold.
      // Only write the key this form actually rendered. The checkbox shows for
      // tech and associate only, so writing it for an owner or manager stores
      // a `false` nobody chose — harmless until the nav began reading
      // permissions, at which point it revokes their own Tech Report on the
      // next innocuous save. PUT /api/users/:id skips the column entirely when
      // `permissions` is absent, so omitting it leaves their overrides alone.
      if (['tech', 'associate'].includes(form.role)) {
        body.permissions = { ...(editingUser?.permissions || {}), canViewAllTechs: body.canViewAllTechs || false };
      } else {
        delete body.permissions;
      }
      delete body.canViewAllTechs;
      if (editingUser && !body.password) delete body.password;
      const res = await fetch(url, { method, headers: authHeaders(true), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save user'); return; }
      if (editingUser) {
        setUsers(prev => prev.map(u => u.id === editingUser.id ? data : u));
        setSuccess('User updated successfully');
      } else {
        setUsers(prev => [...prev, data]);
        setSuccess('User created successfully');
      }
      setShowForm(false);
      setEditingUser(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Connection error');
    }
  }

  async function handleDelete(user) {
    if (!window.confirm(
      `Delete "${user.username}"? This cannot be undone, and anything they sent or created will no longer show their name.\n\nTo keep the history, use Deactivate instead.`
    )) return;
    try {
      const res = await fetch(`${API}/api/users/${user.id}`, { method: 'DELETE', headers: authHeaders() });
      // The server refuses to remove the last active Administrator. Dropping
      // the row from the screen regardless would show a delete that did not
      // happen, and it would come back on the next load.
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        setError(detail.error || `Could not delete that user (${res.status})`);
        return;
      }
      setUsers(prev => prev.filter(u => u.id !== user.id));
      setSuccess('User deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (_) { setError('Connection error'); }
  }

  // Deactivate and reactivate both return the updated row, so the list takes
  // the server's version rather than guessing at it.
  async function setActive(user, active) {
    if (active === false && !window.confirm(
      `Deactivate "${user.username}"? They will not be able to sign in, and their name stays on everything they have done. You can turn this back on any time.`
    )) return;
    setError('');
    try {
      const res = await fetch(`${API}/api/users/${user.id}/${active ? 'reactivate' : 'deactivate'}`, {
        method: 'POST', headers: authHeaders(),
      });
      const detail = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(detail.error || `Could not update that user (${res.status})`);
        return;
      }
      setUsers(prev => prev.map(u => (u.id === detail.id ? { ...u, ...detail } : u)));
      setSuccess(active ? 'User reactivated' : 'User deactivated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (_) { setError('Connection error'); }
  }

  // The server already sorts by role then seniority; splitting preserves that
  // order inside each group.
  const active = users.filter(u => !u.deactivated_at);
  const inactive = users.filter(u => u.deactivated_at);

  function renderUser(user) {
    const off = !!user.deactivated_at;
    // Lightspeed first, so a name corrected there still flows through. Then the
    // name Spinboard stored when the user was created, which is what survives
    // Lightspeed archiving the employee — and also what shows if Lightspeed is
    // slow or down.
    const emp = employees.find(e => e.id === user.employee_id);
    const storedName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    const shownName = emp?.name || storedName;
    // The employee picker deliberately lists ACTIVE employees only, so "not in
    // that list" means Lightspeed no longer counts this person as staff. Worth
    // saying out loud rather than showing a bare name. Not worth saying twice,
    // though — on a deactivated account it tells you nothing new.
    //
    // `employeesLoaded` is the whole point of the condition: a missing match is
    // only evidence once Lightspeed has actually answered. Without it the flag
    // fired on EVERY row for the moment before the list arrived, and forever on
    // a shop with no Lightspeed connection — stating as fact something we had
    // not looked at (18 Sep).
    const goneFromLightspeed = !off && !!user.employee_id && employeesLoaded && !emp;
    const tag = (text, color, title) => (
      <span title={title} style={{
        marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        textTransform: 'uppercase', background: `${color}22`, color, border: `1px solid ${color}72`,
      }}>{text}</span>
    );
    return (
      <div key={user.id} style={{
        background: theme === 'dark' ? '#1e2a40' : '#f4f4ef',
        borderRadius: 8, padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        // Dimmed rather than hidden: still readable, clearly not current staff.
        opacity: off ? 0.62 : 1,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{user.username}</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: `${roleColors[user.role] || '#888'}22`,
              color: roleColors[user.role] || '#888',
              border: `1px solid ${roleColors[user.role] || '#888'}`,
              textTransform: 'uppercase',
            }}>
              {roleLabel(user.role)}
            </span>
            {off && tag('deactivated', '#7f8c8d', 'This account cannot sign in')}
          </div>
          <div style={{ fontSize: 12, color: T.textSub, marginTop: 3 }}>
            {user.email}
            {shownName && (
              <span style={{ marginLeft: 10, color: T.textSub }}>· {shownName} (ID: {user.employee_id})</span>
            )}
            {goneFromLightspeed &&
              tag('not in Lightspeed', '#e67e22', 'This employee is archived in Lightspeed, or no longer exists there')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {off ? (
            <button onClick={() => setActive(user, true)}
              style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: '#2ecc71', borderColor: '#2ecc7144' }}>
              Reactivate
            </button>
          ) : (
            <>
              <button onClick={() => openEdit(user)} style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: T.textSub, borderColor: T.border }}>Edit</button>
              <button onClick={() => setActive(user, false)}
                title="Stops them signing in. Keeps their name on everything they have done."
                style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: T.textSub, borderColor: T.border }}>
                Deactivate
              </button>
            </>
          )}
          <button onClick={() => handleDelete(user)} style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: '#e74c3c', borderColor: '#e74c3c33' }}>Delete</button>
        </div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%', padding: '7px 12px', background: T.input,
    border: `1px solid ${T.inputBorder}`, borderRadius: 6,
    color: T.text, fontSize: 13, fontFamily: 'Arial, sans-serif',
    outline: 'none', boxSizing: 'border-box',
  };

  const roleColors = {
    owner: '#F4622A', manager: '#3498db', tech: '#2ecc71', associate: '#9b59b6'
  };

  if (loading) return null;

  return (
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ ...S.settingsCardTitle, color: T.text }}>User Management</div>
          <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 0 }}>
            Manage who has access to Spinboard and their role.
          </div>
        </div>
        {!showForm && (
          <button
            onClick={openCreate}
            style={{ ...S.btn(true), background: '#F4622A', color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}
          >
            + Add User
          </button>
        )}
      </div>

      {success && (
        <div style={{ background: 'rgba(46,204,113,0.1)', border: '1px solid #2ecc71', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#2ecc71', marginBottom: 12 }}>
          {success}
        </div>
      )}

      {/* User list */}
      {!showForm && (
        <div style={{ display: 'grid', gap: 8 }}>
          {active.map(renderUser)}

          {/* Former staff. Collapsed, because this list only grows. */}
          {inactive.length > 0 && (
            <>
              <button onClick={() => setShowInactive(v => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                  padding: '10px 4px 4px', fontSize: 12, fontWeight: 600, color: T.textSub,
                  display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Arial, sans-serif' }}>
                <span style={{ display: 'inline-block', transition: 'transform 0.15s',
                  transform: showInactive ? 'rotate(90deg)' : 'none' }}>›</span>
                {inactive.length} deactivated {inactive.length === 1 ? 'account' : 'accounts'}
              </button>
              {showInactive && inactive.map(renderUser)}
            </>
          )}
          {users.length === 0 && (
            <div style={{ fontSize: 13, color: T.textSub, textAlign: 'center', padding: 20 }}>
              No users yet. Add your first user above.
            </div>
          )}
        </div>
      )}

      {/* Create/Edit form */}
      {showForm && (
        <form onSubmit={handleSubmit}>
          <div style={{ background: theme === 'dark' ? '#1e2a40' : '#f4f4ef', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 16 }}>
              {editingUser ? `Edit — ${editingUser.username}` : 'New User'}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: T.textSub, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Username</label>
                <input style={inputStyle} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required />
              </div>
              <div>
                <label style={{ fontSize: 11, color: T.textSub, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
                <input type="email" style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: T.textSub, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {editingUser ? 'New Password (leave blank to keep)' : 'Password'}
                </label>
                <input type="password" style={inputStyle} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editingUser} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: T.textSub, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role</label>
                <select style={{ ...inputStyle }} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: T.textSub, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Lightspeed Employee
              </label>
              <select style={inputStyle} value={form.employee_id} onChange={e => {
        const emp = employees.find(emp => emp.id === e.target.value);
        const nameParts = emp?.name?.split(' ') || [];
        setForm({ 
          ...form, 
          employee_id: e.target.value,
          email: emp?.email || form.email,
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' ') || '',
        });
      }} required>
                <option value="">-- Select employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name} · ID: {emp.id}</option>
                ))}
              </select>
              {/* The name Spinboard shows for this person everywhere else (timers,
                  journal, fittings). Copied from the employee picked above;
                  not typed here, so it always matches Lightspeed. */}
              {(() => {
                const picked = [form.first_name, form.last_name].filter(Boolean).join(' ').trim();
                const stored = editingUser ? [editingUser.first_name, editingUser.last_name].filter(Boolean).join(' ').trim() : '';
                const name = picked || stored;
                return (
                  <div style={{ fontSize: 12, color: T.textSub, marginTop: 6 }}>
                    {name
                      ? <>Name shown in Spinboard: <strong style={{ color: T.text }}>{name}</strong>, from Lightspeed</>
                      : 'Name shown in Spinboard: not set yet. It is copied from the employee you pick above.'}
                  </div>
                );
              })()}
            </div>

            {['tech', 'associate'].includes(form.role) && (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.canViewAllTechs || false}
                    onChange={e => setForm({ ...form, canViewAllTechs: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#F4622A' }}
                  />
                  <div>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>Can view all technicians in Tech View</div>
                    <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>By default, Tech and Associate roles only see their own assigned tickets</div>
                  </div>
                </label>
              </div>
            )}
            {error && (
              <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid #e74c3c', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#e74c3c', marginBottom: 12 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" style={{ ...S.btn(true), background: '#F4622A', color: '#fff', fontSize: 13 }}>
                {editingUser ? 'Save Changes' : 'Create User'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingUser(null); }} style={{ ...S.btn(false), color: T.textSub, borderColor: T.border, fontSize: 13 }}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}